import axios, { isAxiosError } from 'axios';

import { auth0Client } from '../auth0Client';
import type { AxiosError, AxiosRequestConfig } from 'axios';

/**
 * Represents an error that occurs during API interactions. It adds clarity to the error logging.
 *
 * @extends Error
 */
export class ApiError extends Error {
  cause: string;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiError';

    // get possible message from cause
    const errMessage = (cause && typeof cause === 'object' && cause.toString()) as
      | string
      | undefined;

    // set stringified cause for cleaner logs.
    this.cause = (errMessage || cause?.constructor.name)!;
  }

  public toString(): string {
    return `${this.name}: ${this.message}`;
  }
}

type FetchOptions = AxiosRequestConfig;

/**
 * Function to fetch data from an API endpoint.
 *
 * @param {string} url - The URL of the API endpoint.
 * @param {object} [options] - Optional options to pass to the fetch function.
 * @returns {Promise<T>} - A promise that resolves to the fetched data.
 * @throws {ApiError} - Throws an error if the fetch fails.
 */
export const fetchApi = async <T>(
  url: string,
  options?: FetchOptions,
  unauthed = false
): Promise<T> => {
  let authorization: string | undefined;
  if (!unauthed) {
    // get access token
    const accessToken = await auth0Client.getAccessTokenSilently();
    if (!accessToken) {
      throw new ApiError('Error fetching id token');
    }
    authorization = `Bearer ${accessToken}`;
  }

  const headers = {
    'Content-Type': 'application/json',
    authorization,
    ...options?.headers,
  };

  const axiosOptions: FetchOptions = {
    method: 'GET',
    ...options,
    headers,
  };

  try {
    const { data } = await axios<T>(url, axiosOptions);
    return data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        const data = {
          message: err.response?.data?.message || err.message,
          error: err.response?.data?.error || err.message,
          status: err.response?.status || 0,
        } as { status: number; message: string };

        const apiError = new ApiError(
          `Error fetching data: [${data.status}] ${err.response?.statusText || ''} - ${data.message}`,
          data
        );
        // capture errors with Sentry
        // Sentry.captureException(apiError, {
        //   tags: { error: 'Fetch API Error' },
        //   extra: { error: apiError, url, options },
        // });

        // Remove unnecessary api stack from errors to clean up console logs
        // delete apiError.stack;

        throw apiError;
      }
    }
    throw err;
  }
};
